import isEmpty from "lodash/isEmpty";

import { AtiraSpacePlan } from "../model/plan/Plan";
import { AtiraSpacePlanPerksCustomFields } from "../model/plan/types/AtiraSpacePlanPerksCustomFields.enum";
import { AtiraSpacePlanPerksEntries } from "../model/plan/types/AtiraSpacePlanPerksEntries.enum";
import { AtiraSpacePlanPerksForms } from "../model/plan/types/AtiraSpacePlanPerksForms.enum";
import { AtiraSpacePlanPerksKanbans } from "../model/plan/types/AtiraSpacePlanPerksKanbans.enum";
import { AtiraSpacePlanPerksUsers } from "../model/plan/types/AtiraSpacePlanPerksUsers.enum";
import { AtiraSpacePlanPlanPerksMobileApp } from "../model/plan/types/AtiraSpacePlanPlanPerksMobileApp.enum";
import { AtiraSpacePlanType } from "../model/plan/types/AtiraSpacePlanType.enum";
import { ThresholdKinds } from "../model/threshold/types/ThresholdKinds.enum";
import { User } from "../model/user/User";
import { AtiraSpacePlanPerksModuleDefinitions } from "../model/plan/types/AtiraSpacePlanPerksModule.enum";
import currencies from "./currencies.json";

export const getPlanTitle = (key: AtiraSpacePlanType) => {
  switch (key) {
    case AtiraSpacePlanType.PLAN_0:
      return "plan.type.plan_0";
    case AtiraSpacePlanType.PLAN_1:
      return "plan.type.plan_1";
    case AtiraSpacePlanType.PLAN_2:
      return "plan.type.plan_2";
  }
};

export const translatePerks = (key: ThresholdKinds, value: Number) => {
  if (key === ThresholdKinds.USERS) {
    switch (value) {
      case AtiraSpacePlanPerksUsers.PLAN_0:
        return "plan.usersCount.plan_0";
      case AtiraSpacePlanPerksUsers.PLAN_1:
        return "plan.usersCount.plan_1";
      case AtiraSpacePlanPerksUsers.PLAN_2:
        return "plan.usersCount.plan_2";
    }
  }

  if (key === ThresholdKinds.ENTRIES) {
    switch (value) {
      case AtiraSpacePlanPerksEntries.PLAN_0:
        return "plan.entriesCount.plan_0";
      case AtiraSpacePlanPerksEntries.PLAN_1:
        return "plan.entriesCount.plan_1";
      case AtiraSpacePlanPerksEntries.PLAN_2:
        return "plan.entriesCount.plan_2";
    }
  }

  if (key === ThresholdKinds.FORMS) {
    switch (value) {
      case AtiraSpacePlanPerksForms.PLAN_0:
        return "plan.formsCount.plan_0";
      case AtiraSpacePlanPerksForms.PLAN_1:
        return "plan.formsCount.plan_1";
      case AtiraSpacePlanPerksForms.PLAN_2:
        return "plan.formsCount.plan_2";
    }
  }

  if (key === ThresholdKinds.KANBANS) {
    switch (value) {
      case AtiraSpacePlanPerksKanbans.PLAN_0:
        return "plan.kanbansCount.plan_0";
      case AtiraSpacePlanPerksKanbans.PLAN_1:
        return "plan.kanbansCount.plan_1";
      case AtiraSpacePlanPerksKanbans.PLAN_2:
        return "plan.kanbansCount.plan_2";
    }
  }

  if (key === ThresholdKinds.CUSTOM_FIELDS) {
    switch (value) {
      case AtiraSpacePlanPerksCustomFields.PLAN_0:
        return "plan.customFieldsCount.plan_0";
      case AtiraSpacePlanPerksCustomFields.PLAN_1:
        return "plan.customFieldsCount.plan_1";
      case AtiraSpacePlanPerksCustomFields.PLAN_2:
        return "plan.customFieldsCount.plan_2";
    }
  }

  if (key === ThresholdKinds.MOBILE_APP) {
    switch (value) {
      case AtiraSpacePlanPlanPerksMobileApp.PLAN_0:
        return "plan.mobileApp.plan_0";
      case AtiraSpacePlanPlanPerksMobileApp.PLAN_1:
        return "plan.mobileApp.plan_1";
      case AtiraSpacePlanPlanPerksMobileApp.PLAN_2:
        return "plan.mobileApp.plan_2";
    }
  }

  if (key === ThresholdKinds.MODULES_DEFINITIONS) {
    switch (value) {
      case AtiraSpacePlanPerksModuleDefinitions.PLAN_0:
        return "plan.modulesDefinitions.plan_0";
      case AtiraSpacePlanPerksModuleDefinitions.PLAN_1:
        return "plan.modulesDefinitions.plan_1";
      case AtiraSpacePlanPerksModuleDefinitions.PLAN_2:
        return "plan.modulesDefinitions.plan_2";
    }
  }
};

const USDollar = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const toUSD = (cents: number) => {
  return USDollar.format(cents / 100);
};

export const toCurrency = (cents: number, currency: string) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return formatter.format(cents / 100);
};

export const getUserCurrentPlan = (user: User) =>
  user.plan?.unpaidPlan?.unpaidPlanType || user.plan?.type;

export const orderedPerksKeys: (keyof NonNullable<
  AtiraSpacePlan["thresholds"]
>["thresholds"])[] = [
  ThresholdKinds.USERS,
  ThresholdKinds.ENTRIES,
  ThresholdKinds.FORMS,
  ThresholdKinds.KANBANS,
  ThresholdKinds.CUSTOM_FIELDS,
  ThresholdKinds.MODULES_DEFINITIONS,
  ThresholdKinds.MOBILE_APP,
];

export function isUserOnPaidPlan(user: User): boolean {
  return !isEmpty(user?.plan) && user.plan.type !== AtiraSpacePlanType.PLAN_0;
}

export const getCurrencySymbol = (currency: string) => {
  try {
    return (
      currencies[currency as keyof typeof currencies]?.symbolNative || currency
    );
  } catch (e) {
    return currency;
  }
};
